/**
 * Keyboard shortcut help overlay (? key).
 *
 * Read-only reference that lists every shortcut wired into the POS
 * billing page (`useKeyboardShortcuts`). Triggered by pressing `?`
 * (Shift+/) when no input is focused. Uses @bbpos/ui Modal for focus
 * trap + escape + backdrop-close, and the Orio360 Glass Depth + gradient
 * accent styling so it matches BillDiscountModal.
 *
 * Keep the list in sync with PAGES.md §2.2 and the switch statement in
 * apps/pos/src/hooks/use-keyboard-shortcuts.ts.
 */

import { Modal } from '@bbpos/ui';

// ─── Shortcut catalog ────────────────────────────────────────────────────────
// Single source of truth. Any change to `useKeyboardShortcuts` should be
// mirrored here so the overlay never lies to the cashier.

interface ShortcutRow {
  keys: string;
  action: string;
  /** Minimum tenant-staff role that can use this shortcut (purely advisory — the
   *  actual gating lives in BillingPage.tsx). */
  minRole: 'CASHIER' | 'MANAGER' | 'OWNER';
}

const SHORTCUTS: readonly ShortcutRow[] = [
  { keys: 'F1', action: 'Focus product search', minRole: 'CASHIER' },
  { keys: 'F2', action: 'Toggle camera barcode scanner', minRole: 'CASHIER' },
  { keys: 'F3', action: 'Open customer lookup', minRole: 'CASHIER' },
  { keys: 'F4', action: 'Park current bill', minRole: 'CASHIER' },
  { keys: 'F5', action: 'Resume parked bill', minRole: 'CASHIER' },
  { keys: 'F8', action: 'Apply bill-level discount', minRole: 'CASHIER' },
  { keys: 'F9', action: 'Open payment modal', minRole: 'CASHIER' },
  { keys: 'F10', action: 'Complete sale (inside payment modal)', minRole: 'CASHIER' },
  { keys: 'F12', action: 'Void current draft bill', minRole: 'MANAGER' },
  { keys: 'Esc', action: 'Close the topmost modal or drawer', minRole: 'CASHIER' },
  { keys: '/', action: 'Focus product search (alternate)', minRole: 'CASHIER' },
  { keys: 'Ctrl + Z', action: 'Remove last added cart item', minRole: 'CASHIER' },
  { keys: '?', action: 'Show this shortcut help overlay', minRole: 'CASHIER' },
];

// ─── Role pill ───────────────────────────────────────────────────────────────

const ROLE_STYLES: Record<ShortcutRow['minRole'], string> = {
  CASHIER:
    'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  MANAGER:
    'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  OWNER:
    'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
};

// ─── Component ───────────────────────────────────────────────────────────────

export interface ShortcutHelpOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function ShortcutHelpOverlay({ open, onClose }: ShortcutHelpOverlayProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="wide"
      title="Keyboard shortcuts"
      closeOnBackdrop
      closeOnEscape
    >
      <div className="px-5 pb-5 pt-2">
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          High-volume billing is faster on the keyboard. Press{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-[10px] font-mono text-neutral-700 dark:text-neutral-300">
            Esc
          </kbd>{' '}
          to close this panel.
        </p>

        <div className="rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20">
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400 w-[140px]"
                >
                  Key
                </th>
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400"
                >
                  Action
                </th>
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400 w-[120px]"
                >
                  Min role
                </th>
              </tr>
            </thead>
            <tbody>
              {SHORTCUTS.map((row, i) => (
                <tr
                  key={row.keys}
                  className={
                    i % 2 === 0
                      ? 'bg-white dark:bg-neutral-900'
                      : 'bg-neutral-50/60 dark:bg-neutral-800/40'
                  }
                >
                  <td className="px-4 py-2.5">
                    <kbd className="inline-flex items-center px-2 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800 text-xs font-mono font-semibold text-brand-700 dark:text-brand-300 shadow-sm tabular-nums">
                      {row.keys}
                    </kbd>
                  </td>
                  <td className="px-4 py-2.5 text-neutral-800 dark:text-neutral-200">
                    {row.action}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={[
                        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold',
                        ROLE_STYLES[row.minRole],
                      ].join(' ')}
                    >
                      {row.minRole}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-4">
          Source: PAGES.md §2.2 · Wired in{' '}
          <code className="font-mono">use-keyboard-shortcuts.ts</code>
        </p>
      </div>
    </Modal>
  );
}
